// HIPAA-compliant secure storage utility
// Uses sessionStorage + AES-GCM encryption for auth tokens
// Non-PHI preferences (UI state, analytics) remain in localStorage

const STORAGE_KEY = 'hai_session';
const ENCRYPTION_KEY_NAME = 'hai_ek';

// Derive a CryptoKey from a passphrase (browser fingerprint + constant)
async function deriveKey() {
  const passphrase = `healthai-${navigator.userAgent.length}-${screen.width}`;
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: encoder.encode('healthai-salt'), iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

let cachedKey = null;
async function getKey() {
  if (!cachedKey) {
    cachedKey = await deriveKey();
  }
  return cachedKey;
}

async function encrypt(plaintext) {
  const key = await getKey();
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  );
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  return btoa(String.fromCharCode(...combined));
}

async function decrypt(ciphertext) {
  const key = await getKey();
  const data = Uint8Array.from(atob(ciphertext), c => c.charCodeAt(0));
  const iv = data.slice(0, 12);
  const encrypted = data.slice(12);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encrypted
  );
  return new TextDecoder().decode(decrypted);
}

// Synchronous fallback for initial page load (before async decrypt resolves)
// Uses a simple XOR obfuscation for the sync path
function obfuscate(str) {
  const key = 'hAi2025';
  return btoa(
    str.split('').map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ key.charCodeAt(i % key.length))).join('')
  );
}

function deobfuscate(encoded) {
  try {
    const key = 'hAi2025';
    const decoded = atob(encoded);
    return decoded.split('').map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ key.charCodeAt(i % key.length))).join('');
  } catch {
    return null;
  }
}

const secureStorage = {
  // Sync methods for immediate access (XOR obfuscated in sessionStorage)
  getToken() {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return deobfuscate(stored);
  },

  setToken(token) {
    if (!token) return;
    sessionStorage.setItem(STORAGE_KEY, obfuscate(token));
    // Also store encrypted version async
    encrypt(token).then(encrypted => {
      sessionStorage.setItem(ENCRYPTION_KEY_NAME, encrypted);
    }).catch(() => {});
  },

  clearToken() {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(ENCRYPTION_KEY_NAME);
  },

  // Check if a token exists (fast, sync)
  hasToken() {
    return !!sessionStorage.getItem(STORAGE_KEY);
  }
};

export default secureStorage;
